import React, { ReactNode, useEffect, useState } from 'react';
import { gql, useLazyQuery, useQuery } from '@apollo/client';

import { Button, IconInfoCircle } from '@trqk/components';
import { queryStrings } from '@trqk/resolvers';
import { getBuildEnvironment } from '@trqk/utils';

import { useModal } from '../../hooks';
import { getFeatureFlags } from '../../featureFlags';

const GET_TRQK_USER = gql`
    query getTrqkUser($id: ID!) {
        getTrqkUser(id: $id) {
            migration_ingestion
        }
    }
`;

const { GET_SESSION_CURRENT_USER } = queryStrings;

interface DataMigrationStatusProps {
    children: ReactNode;
}

const DataMigrationStatusContent = ({
    remove,
}: ModalContentProps<Record<string, never>>): JSX.Element => {
    const buildEnvironment = getBuildEnvironment();

    return (
        <div
            className="rounded-lg bg-white p-10"
            style={{ width: 800, height: 400 }}
        >
            <div className="flex flex-col items-center mt-10">
                <h3 className="mb-12">Down for Maintenance</h3>
                <IconInfoCircle fillColor="quinary-500" width={30} />
                <p className="italic text-tertiary-400 mt-4">
                    TrqkIQ™ is temporarily unavailable due to scheduled
                    maintenance.
                </p>
                <p className="italic text-tertiary-400 mt-2">
                    Please refresh your browser in about an hour.
                </p>
                {buildEnvironment !== 'production' && (
                    <>
                        <Button
                            addClasses="mt-4"
                            autoWidth={true}
                            color="danger-primary-light-bg"
                            handleClick={(): void => {
                                remove({
                                    canceled: false,
                                    success: true,
                                });
                            }}
                        >
                            Dismiss
                        </Button>
                        <div className="italic text-quinary-500">
                            (this button is not shown in production)
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

/**
 * If a user's data is currently being migrated, shows a modal with a warning that TrqkIQ is down
 * for scheduled maintenance. The modal cannot be dismissed, so this blocks the user from doing
 * anything until they refresh after the point that maintenance is finished.
 * @param props - see {@link DataMigrationStatusProps}
 */
export const DataMigrationStatus = ({
    children,
}: DataMigrationStatusProps): JSX.Element => {
    const modal = useModal();
    const currentUserQuery = useQuery(GET_SESSION_CURRENT_USER);
    const userId = currentUserQuery?.data?.session?.currentUser?.id;
    const [getTrqkUser, getTrqkUserQuery] = useLazyQuery<{
        getTrqkUser: TrqkUser;
    }>(GET_TRQK_USER, {
        fetchPolicy: 'network-only',
    });
    const { checkForDataMigration } = getFeatureFlags();

    useEffect(() => {
        if (checkForDataMigration && userId) {
            getTrqkUser({
                variables: {
                    id: userId,
                },
            });
        }
    }, [checkForDataMigration, userId]);

    const trqkUser = getTrqkUserQuery?.data?.getTrqkUser ?? null;

    const ingestionNotComplete =
        trqkUser && trqkUser.migration_ingestion !== 'COMPLETE';

    const [hasModal, setHasModal] = useState(false);

    useEffect(() => {
        if (checkForDataMigration && ingestionNotComplete && !hasModal) {
            modal.add({
                Content: DataMigrationStatusContent,
            });

            setHasModal(true);
        }
    }, [checkForDataMigration, ingestionNotComplete, hasModal]);

    return <>{children}</>;
};
